import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RejectWithMessage, TableDataResponse } from 'features/types';
import { RootState } from 'app/root-reducer';
import { jobSeekersAPI } from "api";
import { getErrorMessage } from 'utils/error';
import { CommunityAdminJobSeekersState, CommunityAdminJobSeekersTabs, JobSeekerTableView, CommunityAdminJobSeekersTableFilters, CommunityAdminJobSeekersTableRequestFiltersData } from './types';
import { Paging, Tab } from 'api/types';

export const getJobSeekersTable = createAsyncThunk<TableDataResponse<JobSeekerTableView>, void, RejectWithMessage>(
  'community-admin-job-seekers/get-community-admin-job-seekers-table',
  async (_, { rejectWithValue, getState }) => {
    try {
      const state: RootState = getState();
      const requestData = { ...state.communityAdminJobSeekers.requestData };
      return await jobSeekersAPI.getCommunityAdminJobSeekersTable(requestData);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const getJobSeekersTabs = createAsyncThunk<Tab[], void, RejectWithMessage>(
  'community-admin-job-seekers/get-community-admin-job-seekers-tabs',
  async (_, { rejectWithValue, getState }) => {
    try {
      const state: RootState = getState();
      const requestData = { ...state.jobSeekerJobSeekers.requestData };
      return jobSeekersAPI.getCommunityAdminJobSeekersTabsData(requestData);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

const initialFilters = {
  text: '',
  tab: CommunityAdminJobSeekersTabs.All,
  location: null,
  fullAddress: '',
  radius: '',
  assignedPrograms: [],
}

const initialRequestData = {
  paging: {
    page: 1,
    pageSize: 20,
  },
  filters: {
    tab: CommunityAdminJobSeekersTabs.All,
    location: null,
    fullAddress: '',
    radius: undefined,
    assignedPrograms: [],
  },
};

const initialState: CommunityAdminJobSeekersState = {
  status: 'idle',
  requestData: initialRequestData,
  currentFilters: initialFilters,
  data: {
    filters: null,
    tableDataIsLoading: false,
    tabsIsLoading: false,
    tabs: [],
    tableData: {
      rows: [],
      totalCount: 0,
      noResult: false,
    },
  },
  error: null,
};

const communityAdminJobSeekersTableSlice = createSlice({
  name: 'community-admins-jobseekers',
  initialState,
  reducers: {
    setPage: (state, action: PayloadAction<Paging>) => {
      state.requestData.paging.page = action.payload.page;
      state.requestData.paging.pageSize = action.payload.pageSize;
    },
    setFilters: (state, action: PayloadAction<Partial<CommunityAdminJobSeekersTableFilters>>) => {
      const payloadFilters = action.payload;
      const newRequestFilters: Partial<CommunityAdminJobSeekersTableRequestFiltersData> = {
        ...payloadFilters,
        assignedPrograms: payloadFilters.assignedPrograms?.map((item) => item.value)
      };
      state.requestData.filters = { ...state.requestData.filters, ...newRequestFilters };
      state.requestData.paging = initialRequestData.paging;

      const newFilters = { ...state.currentFilters, ...action.payload };
      state.currentFilters = newFilters;
    },
    setTab: (state, action: PayloadAction<string>) => {
      state.currentFilters.tab = action.payload;
      state.requestData.filters.tab = action.payload;
    },
    resetRequestData: (state) => {
      state.currentFilters = initialFilters;
      state.requestData = { ...initialState.requestData };
    },
    resetState: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(getJobSeekersTable.pending, (state) => {
      state.data.tableDataIsLoading = true;
    });
    builder.addCase(getJobSeekersTable.fulfilled, (state, action: PayloadAction<TableDataResponse<JobSeekerTableView>>) => {
      state.data.tableDataIsLoading = false;
      state.data.tableData = action.payload;
      state.data.tableData.noResult = state.data.tableData.rows.length === 0;
    });
    builder.addCase(getJobSeekersTable.rejected, (state) => {
      state.data.tableDataIsLoading = false;
    });

    builder.addCase(getJobSeekersTabs.pending, (state) => {
      state.data.tabsIsLoading = true;
    });
    builder.addCase(getJobSeekersTabs.fulfilled, (state, action: PayloadAction<Tab[]>) => {
      state.data.tabsIsLoading = false;
      state.data.tabs = action.payload;
    });
    builder.addCase(getJobSeekersTabs.rejected, (state) => {
      state.data.tabsIsLoading = false;
    });
  },
});

export const { setPage, setTab, setFilters, resetRequestData, resetState } = communityAdminJobSeekersTableSlice.actions;
export const communityAdminJobSeekersTableReducer = communityAdminJobSeekersTableSlice.reducer;
